/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import {
    getGroups,
    IGroup,
    IGroupDelivery,
    IGroups,
    IHelp,
    IOrderConfirmationViewProps,
    IOrderInformation,
    IOrderSummary,
    IPaymentMethods,
    IPriceContext
} from '@msdyn365-commerce-modules/order-management';
import * as MsDyn365 from '@msdyn365-commerce/core';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { Address } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';
import { ISalesLine } from '../../../components/citta-order-management/get-sales-line';
import { getSalesLines, getSalesLinesInput } from '../../../components/citta-order-management/get-sales-lines';
import { IOrderConfirmationConfig, IOrderConfirmationProps as IOrderDetailsExtentionProps, IOrderConfirmationResources as IOrderConfirmationExtentionResources } from '../definition-extensions/order-confirmation.ext.props.autogenerated';
import ShippingAddress from './components/shipping-address';
import { IOrderChannelConfigurationData } from './order-channelConfig.data';
import CittaTelemetry from '../../../common/telemetry/citta-telemetry';
import { PriceComponent } from '@msdyn365-commerce/components';
export interface IOrderInformationExt extends IOrderInformation {
    processingLabelText?: string;
    orderIdLabelText?: string;
}

// private cittaTelemetry: CittaTelemetry = new CittaTelemetry(this.props.context.app.config.cittaInstrumentationKey);

export const OrderConfirmationOrderInfomation: React.FC<IOrderInformationExt> = ({
    orderInformationProps,
    salesId,
    createdDate,
    count,
    amount,
    channelReferenceId,
    channelName,
    channelAddress,
    processingLabelText,
    orderIdLabelText
}) => (
    <Node {...orderInformationProps}>
        {channelName}
        {channelAddress}
        {_renderProcessStatus(salesId, orderIdLabelText!, processingLabelText!)}
        {createdDate}
        {count}
        {amount}
        {channelReferenceId}
    </Node>
);

function _renderProcessStatus(salesId: React.ReactNode, orderIdLabelText: string, processingLabelText: string): React.ReactNode {
    return (
        <span className={typeof salesId === 'string' ? 'ms-order-confirmation__order-information-tracking-processing' : ''}>
            {typeof salesId === 'string' ? (orderIdLabelText?.concat(' ', processingLabelText!)) : salesId}
        </span>
    );
}

export const OrderConfirmationSalesLine: React.FC<ISalesLine> = ({ salesLineProps, salesLine, buyAgainButton }) => (
    <Node {...salesLineProps}>
        {salesLine}
    </Node>
);

export const OrderConfirmationGroupDelivery: React.FC<IGroupDelivery> = ({ deliveryProps, heading, count, processing }) => (
    <Node {...deliveryProps}>
        {heading}
        {count}
    </Node>
);

export const OrderConfirmationGroup: React.FC<IGroup> = ({ groupProps, delivery, salesLinesProps, salesLines }) => (
    <Node {...groupProps}>
        {delivery && <OrderConfirmationGroupDelivery {...delivery} />}
        {salesLines && (
            <Node {...salesLinesProps}>
                {salesLines.map(salesLine => (
                    // @ts-ignore
                    <OrderConfirmationSalesLine key={salesLine.data.salesLine.LineId} {...salesLine} />
                ))}
            </Node>
        )}
    </Node>
);

export const OrderConfirmationGroups: React.FC<IGroups> = ({ groupsProps, groups }) => (
    <Node {...groupsProps}>
        {groups.map((group, index) => (
            <OrderConfirmationGroup key={index} {...group} />
        ))}
    </Node>
);

export const OrderConfirmationOrderSummary: React.FC<IOrderSummary> = ({ orderSummaryProps, heading, subtotal, shipping, tax, totalAmount, earnedPoints }) => (
    <Node {...orderSummaryProps}>
        {heading}
        {subtotal}
        {shipping}
        {tax}
        {totalAmount}
        {earnedPoints}
    </Node>
);

export const OrderConfirmationPayment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {title}
        {methods}
    </Node>
);

export const OrderConfirmationHelp: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);


// const createJsonObject = 


export default class OrderConfirmationView extends React.PureComponent<IOrderConfirmationViewProps & IOrderDetailsExtentionProps<IOrderConfirmationConfig & IOrderChannelConfigurationData> & IOrderConfirmationExtentionResources> {
    public async componentDidMount() {
        if (this.props.data.orderHydration.result) {
            const products = this.props.data.orderHydration.result!.products;
            const salesOrder = this.props.data.orderHydration.result!.salesOrder;
            const productIds = products.map(p => {return `&productIds=${p.RecordId}`});
            let response = await MsDyn365.commerceApiRequest(
                this.props.context.request,
                `CustomGetCategoryPaths${productIds.join('')}`,
                'get'
            ) as any;

            let gaProducts: any[] = [];
            let quantity = 0;

            if (response.status === 200) {
                response.data.forEach((productCategories: any) => {
                    let product = products.find(salesLine => salesLine.RecordId === productCategories.ProductId);
                    let saleeLine = salesOrder.SalesLines && salesOrder.SalesLines.find(line => line.ProductId === productCategories.ProductId);

                    let category = productCategories.CategoryPath && productCategories.CategoryPath.map((pC: any) => {
                        if (pC.Segments[3]) {
                            return pC.Segments[3].CategoryName;
                        } 
                        else if (pC.Segments[2]) {
                            return pC.Segments[2].CategoryName;
                        } 
                        else if (pC.Segments[1]) {
                            return pC.Segments[1].CategoryName;
                        } 
                        else {
                            return null;
                        }
                    });

                    let price = product?.Price;

                    if ((saleeLine?.TotalAmount && saleeLine?.Quantity)) {
                        price = (saleeLine?.TotalAmount / saleeLine?.Quantity)
                    }

                    const productDimensions = product?.Dimensions && product?.Dimensions.map(pD => {
                        return pD.DimensionValue?.Value || "";
                    }).join(" - ")

                    gaProducts.push({
                        "id": product?.RecordId,
                        "sku": product?.ItemId,
                        "name": product?.Name,
                        "quantity": saleeLine?.Quantity,
                        "price": price,
                        "category": category.join(", "),
                        "variant": productDimensions
                    })

                    quantity += (saleeLine?.Quantity || 0);
                }) 

                let data = {
                    "event": "eec.purchase",
                    "ecommerce": {
                        "currencyCode": salesOrder.CurrencyCode,
                        "purchase": {
                            "products": gaProducts,
                            "actionField": {
                                "id": salesOrder.ChannelReferenceId,
                                "shipping": salesOrder.ChargeAmount,
                                "tax": salesOrder.TaxAmount,
                                "revenue": salesOrder.TotalAmount,
                                "affiliation": 'Online Store'
                            },
                            "totalQty": quantity,
                            "orderSummary": {
                                "subTotal": salesOrder.SubtotalAmount,
                                "shipping": salesOrder.ChargeAmount,
                                "tax": salesOrder.TaxAmount,
                                "orderTotal": salesOrder.TotalAmount,
                                "transactionId": salesOrder.Id,
                                "confirmationId": salesOrder.ChannelReferenceId
                            }
                        }
                    }
                }
                
                // @ts-ignore
                if (dataLayer) {
                    // @ts-ignore
                    dataLayer.push(data)
                }
                // @ts-ignore
                if (ecommerce) {
                    // @ts-ignore
                    ecommerce =  {
                        "purchase": {
                            "products": gaProducts,
                            "actionField": {
                                "id": salesOrder.ChannelReferenceId,
                                "shipping": salesOrder.ChargeAmount,
                                "tax": salesOrder.TaxAmount,
                                "revenue": salesOrder.TotalAmount,
                                "affiliation": 'Online Store'
                            }
                        }
                    }
                }
                // @ts-ignore
                if (psdatalayer) {
                    // @ts-ignore
                    psdatalayer = {
                        ecommerce: {
                            purchase: {
                                "products": gaProducts,
                                "actionField": {
                                    "id": salesOrder.ChannelReferenceId,
                                    "shipping": salesOrder.ChargeAmount,
                                    "tax": salesOrder.TaxAmount,
                                    "revenue": salesOrder.TotalAmount,
                                    "affiliation": 'Online Store'
                                }
                            }
                        }
                    }
                }
                

            }
           
        }
    }

    public render(): JSX.Element | null {
        const props = this.props;

        try {
            let cittaTelemetry = new CittaTelemetry(props.context.app.config.cittaInstrumentationKey);
            cittaTelemetry.trace(createLogMessage('landed on order-confirmation page'));
        } catch {}
        const {moduleProps, heading, backToShoppingLink, alert,loading, orderInfomation, orderSummary, payment, config, resources } = props;
        console.log('orderSummary', orderSummary, orderInfomation)
        const priceContext = _getPriceContext(props);
        if (!props.data.orderHydration || ! props.data.orderHydration.result) {
            return (
                <Module {...moduleProps}>
                    {loading}
                </Module>
            )
        }
        const salesLinesInput = getSalesLinesInput(props, 'ms-order-confirmation', props.data.orderHydration.result!.salesOrder, props.data.orderHydration.result!.products, priceContext);
        const salesLines = salesLinesInput && getSalesLines(salesLinesInput);
        const groups = salesLines ? getGroups({
            moduleName: 'ms-order-confirmation',
            salesLines,
            resources: resources
        }) : undefined;

        return (
            <Module {...moduleProps}>
                <div className='ms-order-confirmation__order-confirmation'>
                    {heading}
                    {alert}
                    {loading}
                    {orderInfomation?.channelReferenceId}
                    <span className='ms-order-confirmation__orderSubmittedMessage'>
                        {resources.orderSubmittedMessage}{' '}{config.domain ? config.domain : ''}
                    </span>
                    <span className='emarsys-email'>
                        {orderInfomation?.receiptEmail}
                    </span>
                    {backToShoppingLink}
                </div>
                <Node className='ms-order-confirmation__order-overview-container'>
                    {_renderOrderOverview(props, salesLines, groups)}
                </Node>
                {groups && <OrderConfirmationGroups {...groups} />}
                <div className='msc-orderSummary-pamentMethod'>
                    {_renderOrderSummary(props, salesLines, groups)}
                    {payment && <OrderConfirmationPayment {...payment} />}
                </div>
            </Module>
        );
    }
}

function createLogMessage (message: string) {
    // const url: string = decodeURI(window.location.search);
    // const origin = window.location.origin;
    const logMessage = `${message}}`;
    return logMessage;
}

function _renderOrderOverview (props: IOrderConfirmationViewProps & IOrderDetailsExtentionProps<IOrderConfirmationConfig & IOrderChannelConfigurationData> & IOrderConfirmationExtentionResources,
                               salesLines: ISalesLine[] | undefined, groups: IGroups | undefined): React.ReactNode {
    const giftCardItemId: string | undefined = props.data.channelConfiguration.result?.GiftCardItemId;
    const filteredSalesLines =  salesLines?.filter(s=>s.data.salesLine.ItemId !== giftCardItemId);
    const isB2B = props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits === 'b2b';

    if(filteredSalesLines && filteredSalesLines?.length > 0) {
        return (
            <>
                <div className='ms-order-confirmation__order-information-tracking'>
                    {props.orderInfomation &&
                    <OrderConfirmationOrderInfomation {...props.orderInfomation} processingLabelText = {props.resources.processingLabel} orderIdLabelText = {props.resources.orderIdLabel}/>}
                    {groups && groups.groups[0].delivery.trackingInfo}
                    {salesLines && ( isB2B? null : _renderDeliveryMode(filteredSalesLines, props.resources))}
                </div>
                <div className='ms-order-confirmation__order-shipping-address'>
                    {salesLines && _renderAddress(filteredSalesLines, props.resources)}
                </div>
            </>
            );
    }

    return (
            <div className='ms-order-confirmation__order-information-tracking-full'>
                {props.orderInfomation &&
                <OrderConfirmationOrderInfomation {...props.orderInfomation} processingLabelText = {props.resources.processingLabel} orderIdLabelText = {props.resources.orderIdLabel}/>}
                {groups && groups.groups[0].delivery.trackingInfo}
            </div>
        );
}

function _getPriceContext(props: IOrderConfirmationViewProps & IOrderDetailsExtentionProps<IOrderConfirmationConfig> & IOrderConfirmationExtentionResources): IPriceContext {
    const { id, typeName, context, telemetry, resources: { freePriceText } } = props;
    return {
        id,
        typeName,
        context,
        telemetry,
        freePriceText
    };
}

function _renderDeliveryMode(salesLines: ISalesLine[], resources: IOrderConfirmationExtentionResources): React.ReactNode {
    return (
        <p className='order-confirmation__deliveryMode'>
            {resources.deliveryMode}{': '}
            <span className='order-confirmation__help-content-number'>
                {salesLines[0].data.deliveryType === 'ship' ? 'Shipping' : 'Click & Collect'}
            </span>
        </p>
    );
}
function _renderAddress(salesLines: ISalesLine[], resources: IOrderConfirmationExtentionResources): React.ReactNode {
    const shippingAddress = _getShippingAddress(salesLines);
    return (<ShippingAddress address={shippingAddress} resource={{ shipToLabel: resources.shipToLabel }} />);
}

function _getShippingAddress(salesLinesProps: ISalesLine[]): Address {
    return salesLinesProps[0].data.salesLine.ShippingAddress || {};
}


function _renderOrderSummary(props: IOrderConfirmationViewProps & IOrderDetailsExtentionProps<IOrderConfirmationConfig & IOrderChannelConfigurationData> & IOrderConfirmationExtentionResources,
                            salesLines: ISalesLine[] | undefined, groups: IGroups | undefined): React.ReactNode
{
    const isB2B = props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits === 'b2b';
    const isB2BI = props.context?.request?.apiSettings.oun === "B2BI"

    const {orderSummary} = props;
    if (!orderSummary) {
        return null;
    }
    return (
        <div className='msc-order-summary-wrapper'>
        {orderSummary.heading}
        <div className='msc-order-summary__items'>
            {orderSummary && (
                <>
                    {_renderSubTotal(props)}
                    {isB2B? _renderShippingInclTaxes(props): _renderB2CShippingInclTaxes(props)}
                    {_renderOtherCharges(props)}
                    {/*hide on us*/}
                    {isB2BI ? null : orderSummary.tax}
                    {_renderTotalOrder(props)}
                    {isB2B? null : _renderTotalSavings(props)}
                </>
            )}
        </div>
    </div>
    );
}
function _renderTotalSavings(props: IOrderConfirmationViewProps & IOrderDetailsExtentionProps<IOrderConfirmationConfig & IOrderChannelConfigurationData> & IOrderConfirmationExtentionResources): JSX.Element | null {
    const DiscountAmount = props.data.orderHydration.result?.salesOrder.DiscountAmount;
    return (
        <p className={`msc-order-summary__line--discount-amount`}>
            {DiscountAmount && DiscountAmount > 0 ? (
            <>
                    <span className='msc-order-summary__label'>Total savings</span>
                    <PriceComponent
                        data={{
                            price: { CustomerContextualPrice: DiscountAmount }
                        }}
                        freePriceText={undefined}
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        className={'msc-order-summary__value'}
                    />
                </>
            ) : null}
        </p>
    );
}

function _renderSubTotal(props: IOrderConfirmationViewProps & IOrderDetailsExtentionProps<IOrderConfirmationConfig & IOrderChannelConfigurationData> & IOrderConfirmationExtentionResources): JSX.Element | null {
    const isB2B = props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits === 'b2b';
    let subtotal = 0;
    if (isB2B) {
        subtotal = props.data.orderHydration.result?.salesOrder.NetAmountWithoutTax!;
    }
    else {
        props.data.orderHydration.result?.salesOrder.SalesLines?.forEach((item)=>{
            subtotal += item.Price! * item.Quantity!;
        });
    }
     const OrderSubTotal = subtotal;//props.data.checkout.result?.checkoutCart.cart.NetPrice;
     const isGiftCardInCart = props.data.orderHydration.result?.salesOrder.SalesLines?.some(line => line.ItemId === "GiftCard");
     return (
        <>
         <p className={`msc-order-summary__line-sub-total`}>
            {OrderSubTotal && OrderSubTotal > 0 ? (
             <>
                     <span className='msc-order-summary__label'>{props.resources.orderSummaryItemsTotalLabel}</span>
                     <PriceComponent
                         data={{
                             price: { CustomerContextualPrice: OrderSubTotal }
                         }}
                         freePriceText={undefined}
                         context={props.context}
                         id={props.id}
                         typeName={props.typeName}
                         className={'msc-order-summary__value'}
                     />
                 </>
             ) : null}
         </p>
         <p className={`msc-order-summary__line-sub-total-broken`}>
                     {props.config.tarrifOptionTurnOn && !isGiftCardInCart && OrderSubTotal && OrderSubTotal > 0 ? (
                         <>
                      <span className='msc-order-summary__label'>{'Included Tariff'}</span>
                             <PriceComponent
                                 data={{
                                     price: { CustomerContextualPrice: OrderSubTotal * (props.config.tarrifRate || 0.25)}
                                 }}
                                 freePriceText={undefined}
                                 context={props.context}
                                 id={props.id}
                                 typeName={props.typeName}
                                 className={'msc-order-summary__value'}
                             />
                             </>
                 ) : null}
                 </p>

         </>
     );
 }
 function _renderTotalOrder(props: IOrderConfirmationViewProps & IOrderDetailsExtentionProps<IOrderConfirmationConfig & IOrderChannelConfigurationData> & IOrderConfirmationExtentionResources): JSX.Element | null {
     const OrderTotal = props.data.orderHydration.result?.salesOrder.TotalAmount;
     return (
         <p className={`msc-order-summary__line-order-total`}>
            {OrderTotal && OrderTotal > 0 ? (
             <>
                     <span className='msc-order-summary__label'>{props.resources.orderSummaryGrandTotalLabel}</span>
                     <PriceComponent
                         data={{
                             price: { CustomerContextualPrice: OrderTotal }
                         }}
                         freePriceText={undefined}
                         context={props.context}
                         id={props.id}
                         typeName={props.typeName}
                         className={'msc-order-summary__value'}
                     />
                 </>
             ) : null}
         </p>
     );
 }

 function _renderOtherCharges(props: IOrderConfirmationViewProps & IOrderDetailsExtentionProps<IOrderConfirmationConfig & IOrderChannelConfigurationData> & IOrderConfirmationExtentionResources): JSX.Element | null {
    const chargeAmountInclTax = props.data.orderHydration.result?.salesOrder.ChargeAmount && props.data.orderHydration.result?.salesOrder.ChargeAmount || 0;
    const chargeLines = props.data.orderHydration.result?.salesOrder.ChargeLines;
    const shippingCharges = props.data.orderHydration.result?.salesOrder.ShippingChargeAmount && props.data.orderHydration.result?.salesOrder.ShippingChargeAmount || 0;

    let shippingChargesFromLines = 0;
    let otherChargesAmountFromLines = 0;
    let totalChargeAmountFromLines = 0;
    let chargesOverriden = false;
    let displayOtherChargesAmount = 0;

    const freightChargeCode = props.context.app.config.freightChargeCode;
    if(chargeLines) {
        chargeLines.forEach(chargeLine => {
            const chargeAmountInclTaxLocal = chargeLine.CalculatedAmount ? chargeLine.CalculatedAmount : 0;
            if (chargeLine.ChargeCode != freightChargeCode) {
                otherChargesAmountFromLines += chargeAmountInclTaxLocal;
            }
            if (chargeLine.ChargeCode == freightChargeCode){
                shippingChargesFromLines += chargeAmountInclTaxLocal;
            }
        });
    }

    if(shippingChargesFromLines == 0 &&  shippingCharges != shippingChargesFromLines)
    {
        totalChargeAmountFromLines = shippingCharges + otherChargesAmountFromLines;
    }
    else{
        totalChargeAmountFromLines = shippingChargesFromLines + otherChargesAmountFromLines;
    }

    chargesOverriden = totalChargeAmountFromLines != chargeAmountInclTax;
    displayOtherChargesAmount = chargesOverriden ? 0 : otherChargesAmountFromLines;

    if (displayOtherChargesAmount == 0) {
        return null;
    }
    return (
        <p className={`msc-order-summary__line-shipment`}>
           {displayOtherChargesAmount && displayOtherChargesAmount > 0 ? (
            <>
                    <span className='msc-order-summary__label'>Other charges</span>
                    <PriceComponent
                        data={{
                            price: { CustomerContextualPrice: displayOtherChargesAmount }
                        }}
                        freePriceText={undefined}
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        className={'msc-order-summary__value'}
                    />
                </>
            ) : null}
        </p>
    );
}

function _renderShippingInclTaxes(props: IOrderConfirmationViewProps & IOrderDetailsExtentionProps<IOrderConfirmationConfig & IOrderChannelConfigurationData> & IOrderConfirmationExtentionResources): React.ReactNode {
    const chargeAmountInclTax = props.data.orderHydration.result?.salesOrder.ChargeAmount && props.data.orderHydration.result?.salesOrder.ChargeAmount || 0;
    const chargeLines = props.data.orderHydration.result?.salesOrder.ChargeLines;
    const shippingCharges = props.data.orderHydration.result?.salesOrder.ShippingChargeAmount && props.data.orderHydration.result?.salesOrder.ShippingChargeAmount || 0;

    let shippingChargesFromLines = 0;
    let otherChargesAmountFromLines = 0;
    let totalChargeAmountFromLines = 0;
    let chargesOverriden = false;
    let displayShippingAmount = 0;

    const freightChargeCode = props.context.app.config.freightChargeCode;
    if(chargeLines) {
        chargeLines.forEach(chargeLine => {
            const chargeAmountInclTaxLocal = chargeLine.CalculatedAmount ? chargeLine.CalculatedAmount : 0;
            if (chargeLine.ChargeCode != freightChargeCode) {
                otherChargesAmountFromLines += chargeAmountInclTaxLocal;
            }
            if (chargeLine.ChargeCode == freightChargeCode){
                shippingChargesFromLines += chargeAmountInclTaxLocal;
            }
        });
    }

    if(shippingChargesFromLines == 0 &&  shippingCharges != shippingChargesFromLines)
    {
        totalChargeAmountFromLines = shippingCharges + otherChargesAmountFromLines;
    }
    else{
        totalChargeAmountFromLines = shippingChargesFromLines + otherChargesAmountFromLines;
    }

    chargesOverriden = totalChargeAmountFromLines != chargeAmountInclTax;
    displayShippingAmount = chargesOverriden ? chargeAmountInclTax : shippingChargesFromLines;

    if(shippingChargesFromLines == 0 &&  shippingCharges != shippingChargesFromLines)
    {
        displayShippingAmount = shippingCharges;
    }

    if (displayShippingAmount == 0) {
        return props.orderSummary?.shipping;
    }
    return (
        <p className={`msc-order-summary__line-shipment`}>
           {displayShippingAmount && displayShippingAmount > 0 ? (
            <>
                    <span className='msc-order-summary__label'>{props.resources.orderSummaryShippingFeeLabel}</span>
                    <PriceComponent
                        data={{
                            price: { CustomerContextualPrice: displayShippingAmount }
                        }}
                        freePriceText={undefined}
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        className={'msc-order-summary__value'}
                    />
                </>
            ) : null}
        </p>
    );
}

function _renderB2CShippingInclTaxes(props: IOrderConfirmationViewProps & IOrderDetailsExtentionProps<IOrderConfirmationConfig & IOrderChannelConfigurationData> & IOrderConfirmationExtentionResources): React.ReactNode {
    const chargeLines = props.data.orderHydration.result?.salesOrder.ChargeLines;
    let  chargeAmountInclTax = 0;
    if(chargeLines) {
        chargeLines.forEach(chargeLine => {
            const chargeAmountInclTaxLocal = chargeLine.CalculatedAmount ? chargeLine.CalculatedAmount : 0;
                chargeAmountInclTax += chargeAmountInclTaxLocal;
        });
    }
    if (chargeAmountInclTax == 0) {
        return props.orderSummary?.shipping;
    }
    return (
        <p className={`msc-order-summary__line-shipment`}>
           {chargeAmountInclTax && chargeAmountInclTax > 0 ? (
            <>
                    <span className='msc-order-summary__label'>{props.resources.orderSummaryShippingFeeLabel}</span>
                    <PriceComponent
                        data={{
                            price: { CustomerContextualPrice: chargeAmountInclTax }
                        }}
                        freePriceText={undefined}
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        className={'msc-order-summary__value'}
                    />
                </>
            ) : null}
        </p>
    );
}

// export default OrderConfirmationView;
